import { useState, useEffect, useRef } from "react";
import { useAuth, BACKEND_URL } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, Upload, Menu, Database, CheckCircle, AlertTriangle, ImageIcon, Loader2, FileText, Users } from "lucide-react";
import ArchiveOrdersCard from "@/components/ArchiveOrdersCard";

export default function AdminBackup() {
  const { authAxios } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [file, setFile] = useState(null);
  const [clearExisting, setClearExisting] = useState(false);
  const [result, setResult] = useState(null);
  const [imageInfo, setImageInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [autoBackups, setAutoBackups] = useState(null);
  const [triggering, setTriggering] = useState(false);
  // Duplicati anagrafica clienti
  const [dupStats, setDupStats] = useState(null);
  const [dedupeBusy, setDedupeBusy] = useState(false);
  // Guard so the restore polling loop stops when the page unmounts —
  // otherwise we'd keep calling setState on an unmounted component and
  // hitting the backend forever from navigations mid-restore.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadDupStats = () => {
    authAxios.get("/clients/duplicates-stats").then(r => setDupStats(r.data)).catch(() => {});
  };

  const loadAutoBackups = () => {
    authAxios.get("/backup/list").then(r => setAutoBackups(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/export?part=info`, { credentials: "include" })
      .then(r => r.json())
      .then(setImageInfo)
      .catch(() => {});
    authAxios.get("/backup/stats").then(r => setStats(r.data)).catch(() => {});
    loadAutoBackups();
    loadDupStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios]);

  const handleTriggerBackup = async () => {
    setTriggering(true);
    const tid = toast.loading("Creazione backup sul cloud...");
    try {
      const r = await authAxios.post("/backup/run-now");
      toast.success(`Backup creato: ${(r.data.size_gz_bytes / 1024 / 1024).toFixed(2)} MB compressi`, { id: tid });
      loadAutoBackups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore backup", { id: tid });
    } finally {
      setTriggering(false);
    }
  };

  const handleDownloadAuto = async (backup) => {
    const tid = toast.loading("Download in corso...");
    try {
      const res = await fetch(`${BACKEND_URL}/api/backup/download/${backup.id}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = backup.object_path.split("/").pop().replace(".gz", "");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Backup scaricato", { id: tid });
    } catch (e) {
      toast.error(`Errore: ${e.message}`, { id: tid });
    }
  };

  const handleDeleteAuto = async (backup) => {
    if (!window.confirm(`Eliminare il backup del ${backup.date}?`)) return;
    try {
      await authAxios.delete(`/backup/${backup.id}`);
      toast.success("Backup eliminato");
      loadAutoBackups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const downloadFile = async (part, filename) => {
    const url = `${BACKEND_URL}/api/export?part=${part}`;
    setExporting(true);
    const toastId = toast.loading(`Scaricamento ${filename}...`);
    try {
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${filename} scaricato!`, { id: toastId });
    } catch (error) {
      toast.error(`Errore download: ${error.message}`, { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  const handleExportData = () => {
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "_");
    // Scarica in formato .json.gz compresso (~10x più piccolo): l'upload di
    // ritorno per il restore resta sotto il timeout ingress 60s anche con
    // ~70k sales_rows. Il restore accetta .json.gz nativamente.
    downloadFile("data_gz", `triolo_backup_${stamp}.json.gz`);
  };

  const handleExportImages = (chunkNum) => {
    downloadFile(`images${chunkNum}`, `triolo_images_${chunkNum}.zip`);
    toast.success(`Download immagini parte ${chunkNum} avviato!`);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f && (f.name.endsWith(".json") || f.name.endsWith(".json.gz") || f.name.endsWith(".gz") || f.name.endsWith(".zip"))) {
      setFile(f);
      setResult(null);
    } else {
      toast.error("Seleziona un file .zip, .json o .json.gz");
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    const tid = toast.loading("Preparazione file…");
    try {
      let uploadFile = file;
      const name = (file.name || "").toLowerCase();
      const alreadyCompressed = name.endsWith(".gz") || name.endsWith(".zip");
      // Comprimi client-side i JSON grezzi grandi (>5 MB) per non superare
      // il timeout ingress K8s (60s) durante upload di file da decine di MB.
      // Il backend accetta .json.gz nativamente via magic bytes 1F8B.
      if (!alreadyCompressed && file.size > 5 * 1024 * 1024 && typeof CompressionStream !== "undefined") {
        toast.loading(`Compressione file (${(file.size / 1024 / 1024).toFixed(1)} MB)…`, { id: tid });
        try {
          const stream = file.stream().pipeThrough(new CompressionStream("gzip"));
          const gzBlob = await new Response(stream).blob();
          uploadFile = new File([gzBlob], file.name.replace(/\.json$/i, "") + ".json.gz", { type: "application/gzip" });
          toast.loading(`Compresso: ${(uploadFile.size / 1024 / 1024).toFixed(1)} MB — upload in corso…`, { id: tid });
        } catch (compErr) {
          console.warn("Client-side gzip failed, uploading original file:", compErr);
        }
      } else {
        toast.loading("Caricamento file…", { id: tid });
      }
      const formData = new FormData();
      formData.append("file", uploadFile);
      // Kick off async job — the endpoint streams file to disk and returns job_id in <2s
      const kick = await authAxios.post(`/restore?clear_existing=${clearExisting}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      const jobId = kick.data?.job_id;
      if (!jobId) {
        // Legacy sync response — endpoint returned the result directly
        setResult(kick.data);
        toast.success("Ripristino completato!", { id: tid });
        loadDupStats();
        return;
      }
      // Poll status until done/failed (max 10 minutes)
      const startTs = Date.now();
      const MAX_MS = 10 * 60 * 1000;
      let done = false;
      while (!done) {
        await new Promise((r) => setTimeout(r, 2000));
        if (!mountedRef.current) return; // component unmounted — stop polling
        if (Date.now() - startTs > MAX_MS) {
          throw new Error("Timeout attesa completamento (>10 minuti)");
        }
        const s = await authAxios.get(`/restore/status/${jobId}`);
        if (!mountedRef.current) return;
        const j = s.data || {};
        toast.loading(`Ripristino: ${j.stage || j.status || "in corso"}…`, { id: tid });
        if (j.status === "done") {
          setResult(j.result || {});
          toast.success("Ripristino completato!", { id: tid });
          loadDupStats();
          done = true;
        } else if (j.status === "failed") {
          throw new Error(j.error || "Restore fallito");
        }
      }
    } catch (error) {
      if (!mountedRef.current) return;
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      const timeout = error.code === "ECONNABORTED";
      let msg = detail || error.message || "Errore nel ripristino";
      if (timeout) {
        msg = "Timeout durante l'upload: il file è troppo grande per la rete corrente. Riprova con una connessione più veloce oppure scarica un backup .json.gz dalla produzione (~6 MB)";
      } else if (status && !detail) {
        msg = `Errore ripristino (HTTP ${status})`;
      }
      toast.error(msg, { id: tid });
      console.error("Restore error:", error);
    } finally {
      if (mountedRef.current) setImporting(false);
    }
  };

  const handleDedupe = async () => {
    if (!dupStats || dupStats.duplicate_codes === 0) return;
    const ok = window.confirm(
      `Confermi il dedupe? Verranno cancellati ${dupStats.extra_documents} documenti duplicati ` +
      `(${dupStats.duplicate_codes} codici cliente con doppione). ` +
      `I riferimenti negli ordini/prezzi/regole saranno riassegnati al doc sopravvissuto. ` +
      `L'operazione è irreversibile.`
    );
    if (!ok) return;
    setDedupeBusy(true);
    const tid = toast.loading("Dedupe in corso...");
    try {
      const r = await authAxios.post("/clients/dedupe");
      toast.success(
        `Dedupe completato: ${r.data.documents_deleted} doc rimossi, ` +
        `${r.data.references_moved.orders} ordini riassociati`,
        { id: tid }
      );
      loadDupStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel dedupe", { id: tid });
    } finally {
      setDedupeBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">

      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Backup" />
        <div className="mb-8 flex items-center gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Backup & Ripristino</h1>
            <p className="text-zinc-400 text-sm sm:text-base">Esporta e reimporta dati e immagini</p>
          </div>
        </div>

        {/* Info banner: cosa c'è nel backup */}
        {stats && (
          <div className="mb-6 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
            <p className="text-xs text-amber-400 uppercase tracking-wider font-semibold mb-3">
              Il backup include tutti questi dati:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-center">
              {[
                ["orders", "Ordini", "text-orange-400"],
                ["quotes", "Preventivi", "text-cyan-400"],
                ["delivery_rounds", "Giri", "text-amber-400"],
                ["clients", "Clienti", "text-emerald-400"],
                ["products", "Prodotti", "text-blue-400"],
                ["client_prices", "Prezzi cliente", "text-fuchsia-400"],
                ["discount_rules", "Regole sconto", "text-violet-400"],
                ["giri_presa_ordine", "Giri agenti", "text-sky-400"],
                ["estratti_conto", "Estratti conto", "text-teal-400"],
                ["rules", "AI Rules", "text-pink-400"],
                ["users", "Utenti", "text-zinc-300"],
                ["categories", "Categorie", "text-zinc-300"],
                ["brands", "Marche", "text-zinc-300"],
                ["invite_codes", "Inviti", "text-zinc-300"],
              ].map(([k, label, color]) => (
                <div key={k} className="p-2 rounded-lg bg-black/30 border border-white/5">
                  <p className={`text-xl font-mono font-bold ${color}`} data-testid={`stats-${k}`}>
                    {stats[k] ?? 0}
                  </p>
                  <p className="text-[10px] text-zinc-500">{label}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-amber-300/70 mt-3">
              💡 Consiglio: scarica il backup <strong>ogni sera</strong> e conservane 7 copie sul tuo PC/Drive.
            </p>
          </div>
        )}

        {/* Backup automatici cloud */}
        <Card className="mb-6 bg-[#18181b] border-emerald-500/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-emerald-400" />
              Backup Automatici (Cloud)
            </CardTitle>
            <CardDescription className="text-zinc-400">
              {autoBackups?.retention_days
                ? `Salvataggio automatico ogni notte alle ~${String(autoBackups.daily_hour_utc + 1).padStart(2, "0")}:00 (ora italiana). Ritenzione ${autoBackups.retention_days} giorni.`
                : "Salvataggio automatico notturno sullo storage cloud."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              data-testid="trigger-backup-now-btn"
              onClick={handleTriggerBackup}
              disabled={triggering}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {triggering ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creazione...</>
              ) : (
                <><Database className="w-4 h-4 mr-1" /> Esegui backup ora</>
              )}
            </Button>

            {autoBackups?.items?.length === 0 && (
              <p className="text-zinc-500 text-sm italic">Nessun backup automatico ancora. Clicca "Esegui backup ora" per crearne uno.</p>
            )}

            {autoBackups?.items?.length > 0 && (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {autoBackups.items.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-2 p-3 rounded-lg bg-white/5 border border-white/5">
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-mono truncate" data-testid={`auto-backup-${b.id}`}>
                        {new Date(b.created_at).toLocaleString("it-IT", {
                          day: "2-digit", month: "2-digit", year: "numeric",
                          hour: "2-digit", minute: "2-digit"
                        })}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {(b.size_gz_bytes / 1024 / 1024).toFixed(2)} MB compressi · {b.records?.orders ?? 0} ordini · {b.records?.clients ?? 0} clienti
                        {b.trigger === "scheduler" ? " · automatico" : " · manuale"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => handleDownloadAuto(b)}
                        className="h-8 w-8 text-emerald-400 hover:text-emerald-300"
                        title="Scarica"
                        data-testid={`download-auto-${b.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => handleDeleteAuto(b)}
                        className="h-8 w-8 text-zinc-500 hover:text-red-400"
                        title="Elimina"
                        data-testid={`delete-auto-${b.id}`}
                      >
                        <AlertTriangle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Archivio Ordini Dropbox */}
        <ArchiveOrdersCard authAxios={authAxios} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Export Card */}
          <Card className="bg-[#18181b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Download className="w-5 h-5 text-amber-500" />
                Esporta Dati
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Scarica dati e immagini separatamente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Data export */}
              <div className="p-4 rounded-lg bg-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-amber-400" />
                  <div>
                    <p className="text-sm font-medium text-white">Backup Completo (JSON.GZ)</p>
                    <p className="text-xs text-zinc-500">Ordini, clienti, giri, prodotti, venduto, giacenze, regole — compresso ~6 MB</p>
                  </div>
                </div>
                <Button
                  data-testid="export-data-btn"
                  onClick={handleExportData}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-1" /> Scarica
                </Button>
              </div>

              {/* Image export chunks */}
              {imageInfo && imageInfo.total_chunks > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-400 uppercase tracking-wider">Immagini ({imageInfo.total_images} foto)</p>
                  {imageInfo.chunks.map((chunk, i) => (
                    <div key={`chunk-${chunk.files}-${chunk.size_mb}`} className="p-4 rounded-lg bg-white/5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <ImageIcon className="w-8 h-8 text-purple-400" />
                        <div>
                          <p className="text-sm font-medium text-white">Immagini parte {i + 1}</p>
                          <p className="text-xs text-zinc-500">{chunk.files} foto · {chunk.size_mb} MB</p>
                        </div>
                      </div>
                      <Button
                        data-testid={`export-images-${i + 1}-btn`}
                        onClick={() => handleExportImages(i + 1)}
                        className="bg-purple-500 hover:bg-purple-600 text-white font-medium"
                        size="sm"
                      >
                        <Download className="w-4 h-4 mr-1" /> Scarica
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Import Card */}
          <Card className="bg-[#18181b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Upload className="w-5 h-5 text-emerald-500" />
                Ripristina Dati
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Carica file JSON, JSON.GZ (dati) o ZIP (immagini) per ripristinare
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                file ? "border-emerald-500/50 bg-emerald-500/5" : "border-white/10 hover:border-white/20"
              }`}>
                {file ? (
                  <div className="space-y-2">
                    <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto" />
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-zinc-400 text-sm">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                    <Button variant="ghost" size="sm" onClick={() => { setFile(null); setResult(null); }} className="text-zinc-400 hover:text-red-400">
                      Rimuovi
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Database className="w-10 h-10 text-zinc-500 mx-auto" />
                    <p className="text-zinc-400 text-sm">Seleziona file di backup (.json o .zip)</p>
                    <label>
                      <input type="file" accept=".json,.gz,.zip" onChange={handleFileChange} className="hidden" data-testid="backup-file-input" />
                      <Button type="button" variant="outline" className="border-white/10" onClick={(e) => e.currentTarget.previousElementSibling.click()}>
                        Seleziona File
                      </Button>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-4 bg-red-500/5 rounded-lg border border-red-500/10">
                <div>
                  <Label className="text-white">Cancella dati esistenti</Label>
                  <p className="text-xs text-red-400/70">Elimina tutto prima del ripristino</p>
                </div>
                <Switch checked={clearExisting} onCheckedChange={setClearExisting} data-testid="clear-existing-switch" />
              </div>

              <Button
                data-testid="import-backup-btn"
                onClick={handleImport}
                disabled={!file || importing}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              >
                {importing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Ripristino in corso...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Upload className="w-4 h-4" /> Ripristina Dati
                  </span>
                )}
              </Button>

              <p className="text-xs text-zinc-500 text-center">
                Importa prima il file JSON (dati), poi i file ZIP (immagini) uno alla volta
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Stato anagrafica clienti — alert duplicati */}
        {dupStats && (
          <Card
            className={`mt-6 border-2 ${
              dupStats.duplicate_codes > 0
                ? "bg-amber-500/5 border-amber-500/30"
                : "bg-emerald-500/5 border-emerald-500/20"
            }`}
            data-testid="dedupe-card"
          >
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <Users
                    className={`w-8 h-8 shrink-0 ${
                      dupStats.duplicate_codes > 0 ? "text-amber-400" : "text-emerald-400"
                    }`}
                  />
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm sm:text-base">
                      Anagrafica clienti: <span className="font-mono">{dupStats.total_clients}</span> documenti
                    </p>
                    {dupStats.duplicate_codes > 0 ? (
                      <p className="text-amber-300 text-xs sm:text-sm mt-1" data-testid="dedupe-warning">
                        Rilevati <strong>{dupStats.duplicate_codes}</strong> codici cliente duplicati
                        ({dupStats.extra_documents} doc extra). Dopo il dedupe rimarranno{" "}
                        <strong>{dupStats.expected_after_dedupe}</strong> documenti unici.
                      </p>
                    ) : (
                      <p className="text-emerald-300 text-xs sm:text-sm mt-1">
                        Nessun duplicato rilevato. Anagrafica pulita.
                      </p>
                    )}
                  </div>
                </div>
                {dupStats.duplicate_codes > 0 && (
                  <Button
                    onClick={handleDedupe}
                    disabled={dedupeBusy}
                    className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                    data-testid="dedupe-btn"
                  >
                    {dedupeBusy ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Dedupe in corso...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" /> Esegui Dedupe ({dupStats.extra_documents})
                      </span>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {result && (
          <Card className="mt-6 bg-[#18181b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                {result.errors?.length > 0 ? (
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                )}
                Risultato Ripristino
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {[
                  ["orders_imported", "Ordini", "text-orange-400", "bg-orange-500/10"],
                  ["clients_imported", "Clienti", "text-emerald-400", "bg-emerald-500/10"],
                  ["delivery_rounds_imported", "Giri", "text-amber-400", "bg-amber-500/10"],
                  ["quotes_imported", "Preventivi", "text-cyan-400", "bg-cyan-500/10"],
                  ["products_imported", "Prodotti nuovi", "text-blue-400", "bg-blue-500/10"],
                  ["products_updated", "Aggiornati", "text-sky-400", "bg-sky-500/10"],
                  ["client_prices_imported", "Prezzi custom", "text-fuchsia-400", "bg-fuchsia-500/10"],
                  ["discount_rules_imported", "Regole sconto", "text-violet-400", "bg-violet-500/10"],
                  ["giri_presa_ordine_imported", "Giri agenti", "text-teal-400", "bg-teal-500/10"],
                  ["estratti_conto_imported", "Estratti conto", "text-pink-400", "bg-pink-500/10"],
                  ["categories_imported", "Categorie", "text-zinc-300", "bg-white/5"],
                  ["brands_imported", "Marche", "text-zinc-300", "bg-white/5"],
                  ["users_imported", "Utenti", "text-zinc-300", "bg-white/5"],
                  ["images_restored", "Immagini", "text-purple-400", "bg-purple-500/10"],
                ].map(([k, label, color, bg]) => (
                  <div key={k} className={`p-3 ${bg} rounded-lg text-center`}>
                    <p className={`text-2xl font-bold ${color} font-mono`}>{result[k] || 0}</p>
                    <p className="text-xs text-zinc-400">{label}</p>
                  </div>
                ))}
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-4 bg-red-500/10 rounded-lg p-3">
                  <p className="text-sm text-red-400 mb-2">Errori ({result.errors.length}):</p>
                  {result.errors.slice(0, 5).map((err) => (
                    <p key={`err-${String(err).slice(0, 96)}`} className="text-xs text-red-300 mb-1">{err}</p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
